import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Card } from "@/components/ui/card";
import { resolveOfferRate } from "@/lib/offerRate";
import { rankLabel } from "@/lib/rankLabel";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calculator, Scale, ShieldCheck, MapPin, TrendingUp, Search as SearchIcon, Briefcase, Sparkles } from "lucide-react";
import FirmLogo from "@/components/app/FirmLogo";
import FeaturedFirmsPromo from "@/components/app/featured-firms/FeaturedFirmsPromo";
import PlusCTA from "@/components/app/PlusCTA";
import FeaturedSubjectsPromo from "@/components/app/featured-subjects/FeaturedSubjectsPromo";
import CityFitsQuiz from "@/components/app/CityFitsQuiz";
import EmployerProgressionCard from "@/components/app/EmployerProgressionCard";
import PersonaCard from "@/components/PersonaCard";
import MetroTicket from "@/components/app/MetroTicket";
import ValueSpotlights from "@/components/app/ValueSpotlights";
import FeaturedCitiesPromo from "@/components/app/featured-cities/FeaturedCitiesPromo";
import { useHomeIntro } from "@/lib/useHomeIntro";

/**
 * ReverseMatchInput — the "Where do you want to work?" input that
 * closes the browse loop from Landing → cross-country employer view.
 * Kept inline (local component, not a shared one) because it's a
 * pure one-liner UI: a form that submits to /employers?q=X.
 */
function ReverseMatchInput() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const go = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) return;
    nav(`/employers?q=${encodeURIComponent(v)}`);
  };
  return (
    <form
      onSubmit={go}
      data-testid="landing-reverse-match-form"
      className="max-w-2xl"
    >
      <label className="label-uppercase text-stone-600 flex items-center gap-1.5 mb-2">
        <TrendingUp className="h-3 w-3 text-emerald-700" /> Where do you want to work?
      </label>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Type a firm — e.g. "McKinsey", "Google", "Foreign Office"'
            data-testid="landing-reverse-match-input"
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-stone-300 bg-white text-sm focus:outline-none focus:border-[#4C1D95] focus:ring-1 focus:ring-[#4C1D95]"
          />
        </div>
        <button
          type="submit"
          data-testid="landing-reverse-match-submit"
          className="rounded-md bg-[#4C1D95] text-white text-sm font-medium px-5 hover:bg-[#3B1580] transition"
        >
          Find courses
        </button>
      </div>
      <p className="mt-2 text-[11px] text-stone-500 leading-relaxed">
        We'll show you the UK &amp; US courses whose grads land there — and
        the top 5 whose salaries grow fastest along the way.
      </p>
    </form>
  );
}

const FLAGS = {
  UK: "🇬🇧", US: "🇺🇸", Germany: "🇩🇪", France: "🇫🇷",
  Netherlands: "🇳🇱", Ireland: "🇮🇪", Italy: "🇮🇹", Spain: "🇪🇸",
  Switzerland: "🇨🇭", Australia: "🇦🇺", Canada: "🇨🇦",
  Singapore: "🇸🇬", "United Arab Emirates": "🇦🇪",
  India: "🇮🇳", "South Korea": "🇰🇷",
  Finland: "🇫🇮", Sweden: "🇸🇪", Denmark: "🇩🇰", Norway: "🇳🇴",
  Japan: "🇯🇵", "Hong Kong": "🇭🇰",
};

// Written-out country counts used to live in `countryWord` for the
// (now removed) "N countries. Growing every week." heading. Kept
// `FLAGS` above because `FeaturedCard` still consumes it.


// --- Compact featured-card (landing-only) --------------------------------
// Small horizontal card used exclusively in the "Featured universities"
// section. Deliberately shorter than the full UniCard (which stays intact
// for Explore, Compare picker, etc.). Renders a 96×96 thumbnail + 3 stats.
// Even count so the two-column grid never leaves a lone final card.
const FEATURED_COUNT = 10;

function FeaturedCard({ uni }) {
  const { format } = useCurrency();
  return (
    <Link
      to={`/uni/${uni.id}`}
      data-testid={`featured-uni-${uni.id}`}
      className="block min-w-0"
    >
      <Card className="lift group flex items-stretch gap-4 p-3 pr-5 border-stone-200 bg-white overflow-hidden">
        <div className="w-24 h-24 shrink-0 rounded-md overflow-hidden bg-stone-100 relative">
          <img
            src={uni.photo_url || uni.image_url}
            alt={uni.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { if (uni.image_url && e.target.src !== uni.image_url) e.target.src = uni.image_url; }}
          />
        </div>
        <div className="min-w-0 flex-1 py-1">
          <div className="label-uppercase !text-[10px] text-stone-500">
            {FLAGS[uni.country] || "🎓"} {uni.country}
            {rankLabel(uni) ? ` · ${rankLabel(uni)}` : ""}
          </div>
          <h3 className="font-editorial text-lg mt-0.5 leading-snug truncate">
            {uni.name}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-600">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {uni.city}
            </span>
            <span className="font-mono">
              {(() => {
                const or = resolveOfferRate({ uni });
                if (!or) return "—";
                const isStub = or.source === "legacy_editorial_stub";
                const label = or.metric_kind === "acceptance" ? "accept" : "offer";
                return (
                  <span title={or.label} className={isStub ? "text-stone-400" : ""}>
                    {`${(or.value * 100).toFixed(0)}% ${label}`}
                    {isStub && <span className="ml-0.5 text-[9px] text-amber-700 uppercase">·est</span>}
                  </span>
                );
              })()}
            </span>
            <span className="font-mono">
              {uni.avg_starting_salary_usd != null ? format(uni.avg_starting_salary_usd) : "—"} <span className="text-stone-400">grad</span>
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}


export default function Landing() {
  const [flagships, setFlagships] = useState([]);
  const [catalogueTotal, setCatalogueTotal] = useState(null);
  const [ucasVerified, setUcasVerified] = useState(null); // int | null
  const [heroTicket, setHeroTicket] = useState(null);
  const { format: _format } = useCurrency();  // eslint-disable-line no-unused-vars
  const intro = useHomeIntro();
  const personal = intro.status === "personal";
  const holdHero = intro.status === "pending" ? "invisible" : "";

  useEffect(() => {
    // One small aggregate: the first 10 flagships (one per country, country-count order) + the visible catalogue total.
    api.get("/universities/featured", { params: { limit: FEATURED_COUNT } }).then((r) => {
      setFlagships(r.data?.featured || []);
      setCatalogueTotal(Number.isFinite(r.data?.total) ? r.data.total : null);
    }).catch(() => { setFlagships([]); setCatalogueTotal(null); });
    // Public verification counts — powers the UCAS trust line under
    // the hero. Rounded to the nearest thousand so the number stays
    // legible as we backfill more codes over time.
    api.get("/verify/public-stats")
      .then((r) => setUcasVerified(r?.data?.ucas_live_verified ?? null))
      .catch(() => setUcasVerified(null));
    // Logged-in visitors see their own journey ticket as the hero
    // image; everyone else sees a curated demo so the ticket pattern
    // reads as a product feature, not a login wall.
    api.get("/ticket/me")
      .then(({ data }) => setHeroTicket(data || null))
      .catch(() => setHeroTicket(null));
  }, []);

  // Compact featured strip: server returns flagships already in country-count order (rank, then id per country).
  const featured = flagships;

  // Fallback demo ticket for logged-out visitors — gives the hero
  // something concrete to show rather than a sea of placeholders.
  const demoTicket = useMemo(() => ({
    ticket_id:    "YOURJOURNEY",
    name:         "Your name here",
    origin:       "Your city",
    stopping_at:  "Your top-match uni",
    carriage:     "Your subject",
    destination:  "Your dream job",
    destination_full: "Your dream job",
    match_score:  90,
    completeness: 90,
    has_career:   false,
    variant:      "undecided",
    year_of_entry: 2027,
  }), []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stone-200 hero-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-12 xl:col-span-7">
              <div className="mb-3"><PersonaCard variant="compact" /></div>
              <div className="label-uppercase text-stone-600">Get to Where you are Meant to be</div>
              <h1 data-testid="hero-headline" className={`${holdHero} mt-4 font-editorial text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.05] tracking-tight`}>
                <span className="text-stone-500">
                  Not Which Universities<br />
                  are Best —
                </span>
                <br />
                Which Universities<br />
                <em className="italic text-stone-900 font-normal">are Best For <span className="underline decoration-[#92400E] decoration-4 underline-offset-8 whitespace-nowrap" data-testid="hero-you">{personal && intro.firstName ? intro.firstName : "You"}</span>?</em>
              </h1>
              {/* Mobile ticket — horizontal, sits right under the
                  headline so phones see it before the long paragraph.
                  Hidden on tablet + desktop (desktop version lives in
                  the right-hand grid column). */}
              <div className="mt-8 md:hidden" data-testid="metro-ticket-mobile-slot">
                <div className="w-full max-w-[420px] mx-auto">
                  <MetroTicket
                    data={heroTicket || demoTicket}
                    publicUrl={heroTicket?.ticket_id ? `/journey/${heroTicket.ticket_id}` : null}
                    vertical={false}
                  />
                </div>
              </div>
              {personal ? (
                <p className="mt-8 text-lg text-stone-700 leading-relaxed max-w-2xl" data-testid="hero-personal-intro">
                  {intro.sentences?.length
                    ? <>{intro.sentences.join(" ")} <span className="text-stone-900 font-medium">Let's begin!</span></>
                    : <>Hi{intro.firstName ? ` ${intro.firstName}` : ""}, let's find where you fit. <span className="text-stone-900 font-medium">Start with Fast Match.</span></>}
                </p>
              ) : (
              <p data-testid="hero-generic-intro" className={`${holdHero} mt-8 text-lg text-stone-600 leading-relaxed max-w-2xl`}>
                The only university guide built on <span className="text-stone-900 font-medium">graduate salaries + real employer pipelines</span> — not rankings.
                A senior student never begins with a league table. They ask four practical questions:
                <span className="text-stone-900 font-medium"> Can I get in? Can we afford it?
                Will I enjoy it? Will it take me where I want to go?</span> We built the shortlist —
                and the entire scoring model — around those questions.
              </p>
              )}
              <div className="mt-10 flex flex-wrap gap-3 lg:flex-nowrap" data-testid="hero-cta-row">
                {personal && (
                  <Link to={intro.sentences?.length && intro.resume ? intro.resume.path : "/beta/course-fit"} data-testid="hero-cta-resume">
                    <Button size="lg" className="bg-[#92400E] hover:bg-[#7C350C] text-white rounded-md px-5 whitespace-nowrap">
                      {intro.sentences?.length && intro.resume ? `Continue: ${intro.resume.label}` : "Start Fast Match"} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
                <Link to="/admission">
                  <Button data-testid="hero-cta-chance" size="lg" className="bg-[#002147] hover:bg-[#001A38] text-white rounded-md px-5 whitespace-nowrap">
                    Calculate my chances <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/explore">
                  <Button data-testid="hero-cta-explore" size="lg" variant="outline" className="rounded-md border-stone-800 px-5 whitespace-nowrap">
                    Explore universities
                  </Button>
                </Link>
              </div>
              <div className="mt-6">
                <Link
                  to="/highest-earning"
                  data-testid="hero-cta-highest-earning"
                  className="group inline-flex items-center gap-2 text-sm text-[#7C3AED] hover:text-[#5B21B6] font-medium"
                >
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#7C3AED] animate-pulse" />
                  See UK courses where grads earn £45K+ three years out
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              {/* UCAS data-provenance line — a subtle trust signal
                  showing that our UCAS codes aren't guessed. Rounded
                  to the nearest thousand so the phrasing stays natural
                  as the number grows. Hidden while the number is still
                  loading to avoid a flash-of-zero. */}
              {typeof ucasVerified === "number" && ucasVerified > 1000 && (
                <div
                  className="mt-5 inline-flex items-center gap-2 text-[12px] text-stone-600"
                  data-testid="ucas-provenance-banner"
                >
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" aria-hidden />
                  <span>
                    <span
                      className="font-semibold text-stone-800 font-mono"
                      data-testid="ucas-provenance-count"
                    >
                      {Math.floor(ucasVerified / 1000).toLocaleString()},000+
                    </span>{" "}
                    UCAS course codes cross-checked against UCAS itself
                    <span className="text-stone-400"> — no ranking guesswork.</span>
                  </span>
                </div>
              )}
              {/* Flagship strip — one honest line linking to the
                  hand-checked catalogue. The count is baked in and
                  bumped on every flagship-pack release; this is
                  cheaper (and more honest) than a live API roundtrip
                  because the source of truth is the seed script. */}
              <div className="mt-3">
                <Link
                  to="/flagships"
                  data-testid="hero-flagship-strip"
                  className="group inline-flex items-center gap-2 text-[12px] text-stone-600 hover:text-emerald-800 transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5 text-emerald-700" aria-hidden />
                  <span>
                    <span
                      className="font-semibold text-stone-800 font-mono"
                      data-testid="hero-flagship-count"
                    >
                      115
                    </span>{" "}
                    hand-checked bachelors across{" "}
                    <span className="font-semibold text-stone-800">13 countries</span>
                    <span className="text-stone-400"> — verified against each university's own page.</span>
                  </span>
                  <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" aria-hidden />
                </Link>
              </div>
            </div>
            <div className="hidden xl:block xl:col-span-5">
              <div className="w-[450px] lg:ml-auto lg:mr-0 lg:translate-y-[72px]">
                <MetroTicket
                  data={heroTicket || demoTicket}
                  publicUrl={(heroTicket?.ticket_id || demoTicket.ticket_id)
                    ? `/journey/${(heroTicket?.ticket_id || demoTicket.ticket_id).toLowerCase()}`
                    : null}
                  vertical={true}
                  fixedScale={1.17}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <FeaturedCitiesPromo />

      {/* TOOLS BENTO — one-click discoverability for the six
          first-class products we ship. The "four practical
          questions" language stays in the hero copy (that's
          positioning); this section is about actionable tools. */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="landing-tools-section">
        <div className="label-uppercase">The toolkit</div>
        <h2 className="font-editorial text-3xl sm:text-4xl mt-3 max-w-3xl">
          Six tools. One honest shortlist.
        </h2>
        <p className="mt-4 text-stone-600 max-w-2xl leading-relaxed">
          Every product below runs on the same evidence-verified corpus —
          real UCAS codes, real graduate salaries, real per-country policy
          gates. No prestige guesswork.
        </p>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              icon: Sparkles, title: "Fast Match",
              body: "A ~3-minute prompt that asks about your course interests and how you like to study, then shows a small cluster of courses that match.",
              cta: "Try Fast Match", to: "/beta/course-fit",
              testId: "landing-tool-fast-match",
              accent: "bg-[#F5F0FF] border border-[#E5DBFF]",
              iconAccent: "text-[#4C1D95]",
              badge: null,
            },
            {
              icon: Calculator, title: "Chance Calculator",
              body: "Enter curriculum, grades, tests, activities — every uni returns a Likely / Target / Ambitious verdict grounded in that year's admissions data.",
              cta: "Run the calculator", to: "/admission",
              testId: "landing-tool-admission",
              accent: "bg-[#002147] text-white",
              iconAccent: "text-white/90",
              badge: null,
            },
            {
              icon: Sparkles, title: "Advanced Match",
              body: "The 100-input personalised shortlist — cost envelope, PSW window, subject fit, culture, bucket buckets. What families upgrade to when they're serious.",
              cta: "Open Advanced", to: "/advanced",
              testId: "landing-tool-advanced",
              accent: "bg-white border border-stone-300",
              iconAccent: "text-[#4C1D95]",
              badge: null,
            },
            {
              icon: ShieldCheck, title: "Flagship 50+",
              body: "115 hand-checked bachelors across 13 countries. Each record verified against the university's own page — honest tuition, honest offer rate, honest gotchas.",
              cta: "Browse flagships", to: "/flagships",
              testId: "landing-tool-flagships",
              accent: "bg-emerald-50 border border-emerald-300",
              iconAccent: "text-emerald-800",
              badge: "115 · uni-verified",
            },
            {
              icon: Scale, title: "Compare Programmes",
              body: "Rankings are too blunt. Compare specific courses side by side — modules, class size, contact hours, accreditation, honest employability.",
              cta: "Open Compare", to: "/compare",
              testId: "landing-tool-compare",
              accent: "bg-white border border-stone-300",
              iconAccent: "text-stone-800",
              badge: null,
            },
            {
              icon: Briefcase, title: "Highest-Earning UK",
              body: "The UK bachelors whose grads land £45k+ three years out — filtered from ONS Longitudinal Earnings Outcomes (LEO), not brochure claims.",
              cta: "See the list", to: "/highest-earning",
              testId: "landing-tool-highest-earning",
              accent: "bg-[#F3F1EC] border border-stone-300",
              iconAccent: "text-[#7C3AED]",
              badge: null,
            },
          ].map((c) => (
            <Link
              key={c.title}
              to={c.to}
              data-testid={c.testId}
              className={`group rounded-lg p-6 lift transition-all ${c.accent}`}
            >
              <div className="flex items-start justify-between gap-3">
                <c.icon className={`h-6 w-6 ${c.iconAccent}`} aria-hidden />
                {c.badge && (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full px-2 py-0.5 ${
                    c.badge === "LIVE"
                      ? "bg-rose-600 text-white animate-pulse"
                      : c.accent.includes("bg-[#002147]")
                        ? "bg-white/15 text-white"
                        : "bg-white border border-emerald-400 text-emerald-800"
                  }`}>
                    {c.badge}
                  </span>
                )}
              </div>
              <h3 className={`font-editorial text-2xl mt-5 leading-tight ${c.accent.includes("text-white") ? "text-white" : ""}`}>
                {c.title}
              </h3>
              <p className={`mt-3 text-sm leading-relaxed ${
                c.accent.includes("text-white") ? "text-white/85" : "text-stone-600"
              }`}>
                {c.body}
              </p>
              <div className={`mt-5 text-xs label-uppercase inline-flex items-center gap-1 ${
                c.accent.includes("text-white")
                  ? "text-white/90 group-hover:text-white"
                  : "text-[#002147] group-hover:text-[#92400E]"
              } transition-colors`}>
                {c.cta}
                <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" aria-hidden />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FIRM TILES — career-outcomes-first browse. Six flagship firms
          across banking / consulting / tech / law / big-4 / asset-mgmt
          per country so international families see immediately that
          the site indexes destinations, not rankings. A country
          chooser above the tiles swaps the roster (UK/US → salary-
          lift firms, IE/DE/FR/IT/ES/PT → curated regional hubs). */}
      <FeaturedFirmsPromo />

      {/* CITY FITS QUIZ — 60-sec homepage widget */}
      <div id="city-quiz" className="scroll-mt-24">
        <CityFitsQuiz />
      </div>

      {/* NOTE: standalone COUNTRIES tile-grid was removed 2026-02.
          TrustDashboard below already exposes country tiles WITH
          verification % — same shortcut, more useful information.
          Removed section was 20 duplicate country tiles pointing at
          `/explore?country=X` with no evidence signal. */}

      <FeaturedSubjectsPromo />

      {/* NOTE: TrustDashboard (country grid with URL-level verification
          %) was removed from the homepage 2026-02 per Owner. The
          VerifiedDepthStrip above already carries the per-country
          coverage signal without the flag-tile duplication. */}

      {/* CAREER-ACCELERATOR EMPLOYERS — compact strip. This is the
          differentiator that turns "we have university data" into
          "we have real progression data no other UK guide publishes". */}
      <section className="bg-[#FBF8F3] border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          {/* Reverse-match input — a family types the firm they
              aspire to (McKinsey, Google, Foreign Office) and lands
              on /employers with cross-country results pre-filtered.
              Sits above the ranked strip because "type where you
              want to work" is the more actionable ask than "browse
              our ranked list". Form-based so Enter submits. */}
          <ReverseMatchInput />
          <div className="mt-8">
            <EmployerProgressionCard
              variant="compact"
              limit={6}
              minAppearances={3}
              testid="landing-employer-progression"
            />
          </div>
        </div>
      </section>

      {/* UK VISA-FRIENDLY CALLOUT — SEO magnet for international parents */}
      <section className="bg-[#002147] border-y border-stone-900" data-testid="landing-visa-friendly-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="text-stone-100 max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-underground-sc tracking-wider text-emerald-300">
              <ShieldCheck className="w-4 h-4" />
              For international families
            </div>
            <h2 className="font-editorial text-3xl sm:text-4xl mt-3 leading-tight">
              Three shortlists you can't build with a generic ranking.
            </h2>
            <p className="mt-3 text-stone-300">
              Every degree on the lists below has been editorially verified against the source institution's programme page. No AI-generated fluff, no scraped course lists — real data on the specific pathways your family is trying to compare.
            </p>
          </div>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/uk-visa-friendly-degrees"
              data-testid="landing-visa-friendly-cta"
              className="group bg-white/5 hover:bg-white/10 border border-stone-700 hover:border-stone-500 rounded-lg p-6 transition-colors block"
            >
              <div className="flex items-center gap-2 text-xs font-underground-sc tracking-wider text-emerald-300">
                🇬🇧 UK · Skilled Worker
              </div>
              <div className="font-editorial text-2xl text-stone-100 mt-2">
                UK Shortage Occupation shortlist
              </div>
              <div className="text-sm text-stone-300 mt-2 leading-relaxed">
                Nursing, Cyber, Aerospace, Optometry, Pharmacy MPharm — degrees that unlock the lowest-friction UK Skilled Worker route.
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-white group-hover:gap-3 transition-all">
                See UK shortlist <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
            <Link
              to="/us-stem-opt-degrees"
              data-testid="landing-stem-opt-cta"
              className="group bg-white/5 hover:bg-white/10 border border-stone-700 hover:border-stone-500 rounded-lg p-6 transition-colors block"
            >
              <div className="flex items-center gap-2 text-xs font-underground-sc tracking-wider text-emerald-300">
                🇺🇸 US · STEM OPT
              </div>
              <div className="font-editorial text-2xl text-stone-100 mt-2">
                US STEM OPT shortlist
              </div>
              <div className="text-sm text-stone-300 mt-2 leading-relaxed">
                AI, Data Science, Cybersecurity, Nursing BSN, Business Analytics — STEM-designated degrees that unlock the 3-year OPT (vs 1-year non-STEM).
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-white group-hover:gap-3 transition-all">
                See US shortlist <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
            <Link
              to="/us-liberal-arts-colleges"
              data-testid="landing-lac-cta"
              className="group bg-white/5 hover:bg-white/10 border border-stone-700 hover:border-stone-500 rounded-lg p-6 transition-colors block"
            >
              <div className="flex items-center gap-2 text-xs font-underground-sc tracking-wider text-amber-300">
                🇺🇸 US · Liberal Arts
              </div>
              <div className="font-editorial text-2xl text-stone-100 mt-2">
                US Liberal Arts Colleges
              </div>
              <div className="text-sm text-stone-300 mt-2 leading-relaxed">
                Amherst, Williams, Swarthmore, Pomona, Harvey Mudd, Wellesley — 30 elite LACs with cohorts under 4,000 and Ivy-Plus PhD placement rates.
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-white group-hover:gap-3 transition-all">
                See the 30 LACs <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
            <Link
              to="/uk-conservatoires"
              data-testid="landing-conservatoire-cta"
              className="group bg-white/5 hover:bg-white/10 border border-stone-700 hover:border-stone-500 rounded-lg p-6 transition-colors block"
            >
              <div className="flex items-center gap-2 text-xs font-underground-sc tracking-wider text-purple-300">
                🇬🇧 UK · Conservatoires
              </div>
              <div className="font-editorial text-2xl text-stone-100 mt-2">
                UK Conservatoires
              </div>
              <div className="text-sm text-stone-300 mt-2 leading-relaxed">
                RADA, RCM, RAM, Guildhall, Royal Central, Trinity Laban — audition-based training that pipelines directly to the National Theatre, LSO and West End.
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-white group-hover:gap-3 transition-all">
                See the 11 conservatoires <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED UNIS — compact 10-flagship strip (one per country). Full
          catalogue browsing lives on /explore; this section is a taste. */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-editorial text-2xl sm:text-3xl">Featured universities</h2>
          </div>
          <Link
            to="/explore"
            data-testid="landing-see-all-unis"
            className="text-sm text-[#4C1D95] hover:text-[#3B0764] inline-flex items-center gap-1"
          >
            Browse all {catalogueTotal != null ? `${catalogueTotal.toLocaleString()} ` : ""}universities <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="landing-featured-grid">
          {featured.map((u) => <FeaturedCard key={u.id} uni={u} />)}
        </div>
      </section>

      {/* Jump-off to WUA+ (marketing funnel entry point) */}
      <PlusCTA testId="landing-plus-cta" />

      {/* UK CLEARING 2026 — standalone, time-sensitive section.
          Kept out of the six-tool bento so the toolkit stays balanced;
          Clearing is a seasonal, live-data product with its own beat.
          Owner-approved 2026-02: promoted to sit ABOVE the toolkit so
          the time-sensitive live product leads the tool discovery. */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-10 pt-16"
        data-testid="landing-clearing-section"
      >
        <Link
          to="/clearing"
          data-testid="landing-tool-clearing"
          className="group block rounded-lg p-8 lg:p-10 lift transition-all bg-rose-50 border border-rose-300"
        >
          <div className="grid lg:grid-cols-[1fr,auto] gap-8 items-center">
            <div>
              <div className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-rose-800" aria-hidden />
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full px-2 py-0.5 bg-rose-600 text-white animate-pulse"
                >
                  LIVE
                </span>
                <span className="label-uppercase text-rose-900/80">
                  Seasonal · UCAS Clearing 2026
                </span>
              </div>
              <h2 className="font-editorial text-3xl sm:text-4xl mt-4 leading-tight text-stone-900">
                UK Clearing 2026
              </h2>
              <p className="mt-4 text-stone-700 max-w-2xl leading-relaxed">
                Live grades in, matching universities out. Real-time UCAS
                Clearing feed with a deep-link into the correct clearing
                hotline for every offer — so you spend results morning
                calling admissions, not googling numbers.
              </p>
            </div>
            <div className="text-sm label-uppercase inline-flex items-center gap-1 text-[#002147] group-hover:text-[#92400E] transition-colors">
              Open Clearing
              <ArrowRight
                className="h-3 w-3 group-hover:translate-x-0.5 transition-transform"
                aria-hidden
              />
            </div>
          </div>
        </Link>
      </section>

      {/* Best-value spotlights — 3-card trio for guests, 1 personalised
          pick for signed-in students. Sources /api/value/spotlights. */}
      <ValueSpotlights testId="landing-value-spotlights" />
    </div>
  );
}
